import { NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, inject } from '@angular/core';
import { toSignal } from '@angular/core/rxjs-interop';
import { NavigationEnd, Router, RouterLink, RouterLinkActive } from '@angular/router';
import { filter, map, startWith } from 'rxjs';
import { PermissionsDirective } from '../../auth/directives/permissions.directive';
import { DamagePermissions, NewDamagePermissions, NewScreenPermissions, Permission } from '../../auth/models/permissions';
import { EnvironmentService } from '../../environment';
import { UserSectionComponent } from './user-section/user-section.component';

interface NavItem {
  route: string;
  icon: string;
  label: string;
  /** Screen shown only when the user holds at least one of these permissions. */
  permissions: Permission[];
}

@Component({
  selector: 'app-navigation-panel',
  imports: [RouterLink, RouterLinkActive, UserSectionComponent, NgOptimizedImage, PermissionsDirective],
  templateUrl: './navigation-panel.component.html',
  styleUrl: './navigation-panel.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class NavigationPanelComponent {
  private readonly environmentService = inject(EnvironmentService);
  private readonly router = inject(Router);

  protected readonly navItems: NavItem[] = [
    {
      route: '/damage-review',
      icon: 'pi pi-exclamation-triangle',
      label: 'New Submissions',
      permissions: [DamagePermissions.DAMAGE_REVIEW_SCREEN, NewDamagePermissions.DAMAGE_REVIEW_SCREEN],
    },
    { route: '/tasks', icon: 'pi pi-check-square', label: 'Tasks For Me', permissions: [NewScreenPermissions.TASKS] },
    { route: '/in-progress', icon: 'pi pi-clock', label: 'In Progress', permissions: [NewScreenPermissions.IN_PROGRESS] },
    { route: '/invoiced', icon: 'pi pi-credit-card', label: 'Invoiced', permissions: [NewScreenPermissions.INVOICED] },
    { route: '/all-incidents', icon: 'pi pi-th-large', label: 'All Incidents', permissions: [NewScreenPermissions.ALL_INCIDENTS] },
    {
      route: '/damage-form',
      icon: 'pi pi-plus-circle',
      label: 'Submit New',
      permissions: [DamagePermissions.CREATE_DAMAGE_INPUT, NewDamagePermissions.CREATE_DAMAGE_INPUT],
    },
  ];

  protected readonly showPermissionOverrides = this.environmentService.isPermissionOverrideEnvironment;

  protected readonly isAllIncidentsActive = toSignal(
    this.router.events.pipe(
      filter(e => e instanceof NavigationEnd),
      map(() => this.router.url),
      startWith(this.router.url),
      map(url => url.startsWith('/all-incidents') || url.startsWith('/incidents/')),
    ),
    { initialValue: false },
  );
}
